import { formatHumanName, resolveId } from "@medplum/core";
import type {
  Invoice,
  Organization,
  Patient,
  PaymentReconciliation,
  PaymentReconciliationDetail,
} from "@medplum/fhirtypes";

import { compareOptionalInstant } from "@/billing/components/PaymentsTab/utils/ledger-instant";
import { EMPTY_VALUE } from "@/billing/constants/empty-value";
import { LEDGER_DETAIL_TYPE, type LedgerDetailType } from "@/billing/constants/ledger-detail-type";
import { LEDGER_SOURCE } from "@/billing/constants/ledger-source";
import {
  DEFAULT_PRACTICE_LEDGER_SORT,
  EMPTY_PRACTICE_LEDGER_TOTALS,
  PRACTICE_LEDGER_ROW_TYPE,
  PRACTICE_LEDGER_SEARCH_OPTION,
  PRACTICE_LEDGER_SOURCE_KIND,
  type PracticeLedgerDateRange,
  type PracticeLedgerRow,
  type PracticeLedgerRowType,
  type PracticeLedgerSearch,
  type PracticeLedgerSortState,
  type PracticeLedgerTotals,
} from "@/billing/types/practice-ledger";
import { statementNumberOf } from "@/billing/utils/invoice-statement-number";
import { getDetailType, isPaymentReversalDetail } from "@/billing/utils/ledger-detail-type";
import {
  getLedgerEntrySourceLabel,
  getLedgerSource,
  getLedgerSourceLabel,
  getPaymentIssuerOrganizationId,
} from "@/billing/utils/ledger-source";
import { isCalendarDateInRange, toCalendarDate } from "@/billing/utils/practice-ledger-date-range";
import { predecessorNumber } from "@/billing/utils/statement-numbers";
import { getPatientMrn } from "@/clients/utils/mrn";
import { joinReferenceString } from "@/utils/medplum";

export interface PracticeLedgerFlattenInput {
  windowInvoices: Invoice[];
  paymentReconciliations: PaymentReconciliation[];
  invoicesByStatementNumber: Map<string, Invoice>;
  patientsById: Map<string, Patient>;
  organizationsById: Map<string, Organization>;
  dateRange: PracticeLedgerDateRange;
}

/**
 * Turns windowed Invoices and PaymentReconciliations into Recent Activity rows.
 *
 * Charge rows come only from `windowInvoices` (statements whose `Invoice.date` is in the Show:
 * window). A next-payer sub-statement (`0037-1`) re-bills its primary's charges, so it adds no
 * Charge row of its own. A window that holds only a sub-statement shows no charge, since the
 * practice charged the client when the primary was issued. Payment, Payment Reversal and
 * Adjustment rows come from active `payment` and `adjustment` details whose transaction date falls
 * in that window, even when the statement itself does not.
 */
export function flattenPracticeLedgerRows(input: PracticeLedgerFlattenInput): PracticeLedgerRow[] {
  const chargeRows = input.windowInvoices.reduce<PracticeLedgerRow[]>((rows, invoice) => {
    const row = chargeRowFrom(invoice, input.patientsById, input.organizationsById);
    if (row && isCalendarDateInRange(row.transactionDate, input.dateRange)) rows.push(row);
    return rows;
  }, []);

  const postingRows = input.paymentReconciliations.reduce<PracticeLedgerRow[]>(
    (rows, paymentReconciliation) => {
      if (paymentReconciliation.status !== "active") return rows;

      const statementNumber = statementNumberOf(paymentReconciliation);
      const invoice = statementNumber
        ? input.invoicesByStatementNumber.get(statementNumber)
        : undefined;
      if (!invoice) return rows;

      return (paymentReconciliation.detail ?? []).reduce((detailRows, detail, index) => {
        const row = detailRowFrom(
          paymentReconciliation,
          detail,
          index,
          invoice,
          input.patientsById,
          input.organizationsById,
        );
        if (row && isCalendarDateInRange(row.transactionDate, input.dateRange)) {
          detailRows.push(row);
        }
        return detailRows;
      }, rows);
    },
    [],
  );

  return [...chargeRows, ...postingRows];
}

/**
 * Window KPIs: charge amounts sum into `totalCharges`; payment and payment reversal amounts sum into
 * `totalReceived` and split by source. Adjustments are not cash, so no KPI counts them.
 */
export function sumPracticeLedgerTotals(rows: PracticeLedgerRow[]): PracticeLedgerTotals {
  return rows.reduce<PracticeLedgerTotals>(
    (totals, row) => {
      if (row.type === PRACTICE_LEDGER_ROW_TYPE.Charge) {
        return { ...totals, totalCharges: totals.totalCharges + row.amount };
      }

      if (
        row.type !== PRACTICE_LEDGER_ROW_TYPE.Payment &&
        row.type !== PRACTICE_LEDGER_ROW_TYPE.PaymentReversal
      ) {
        return totals;
      }

      const fromInsurance =
        row.sourceKind === PRACTICE_LEDGER_SOURCE_KIND.Insurance
          ? totals.fromInsurance + row.amount
          : totals.fromInsurance;
      const fromClients =
        row.sourceKind === PRACTICE_LEDGER_SOURCE_KIND.Client
          ? totals.fromClients + row.amount
          : totals.fromClients;

      return {
        ...totals,
        totalReceived: totals.totalReceived + row.amount,
        fromClients,
        fromInsurance,
      };
    },
    { ...EMPTY_PRACTICE_LEDGER_TOTALS },
  );
}

/**
 * Narrows rows by client name or insurance/source label. An empty query returns the rows unchanged.
 */
export function filterPracticeLedgerRows(
  rows: PracticeLedgerRow[],
  search: PracticeLedgerSearch | undefined,
): PracticeLedgerRow[] {
  const normalized = search?.query.trim().toLowerCase() ?? "";
  if (!normalized) return rows;

  if (search?.option === PRACTICE_LEDGER_SEARCH_OPTION.Insurance) {
    return rows.filter((row) => row.sourceLabel.trim().toLowerCase().includes(normalized));
  }

  return rows.filter((row) => row.clientName.trim().toLowerCase().includes(normalized));
}

/** Newest transaction date first by default; statement number then row id break ties. */
export function sortPracticeLedgerRows(
  rows: PracticeLedgerRow[],
  sort: PracticeLedgerSortState = DEFAULT_PRACTICE_LEDGER_SORT,
): PracticeLedgerRow[] {
  const direction = sort.direction;
  return [...rows].sort((left, right) => {
    const dateResult = compareOptionalInstant(
      left.transactionDate,
      right.transactionDate,
      direction,
    );
    if (dateResult !== 0) return dateResult;

    const statementResult = (left.statementId ?? "").localeCompare(
      right.statementId ?? "",
      undefined,
      { numeric: true },
    );
    if (statementResult !== 0) return statementResult;

    return left.id.localeCompare(right.id);
  });
}

/**
 * Returns one page of an already-sorted row set. `page` is 1-based; values below 1 are treated as
 * page 1. A page past the end is an empty list.
 */
export function paginatePracticeLedgerRows(
  rows: PracticeLedgerRow[],
  page: number,
  pageSize: number,
): PracticeLedgerRow[] {
  const currentPage = page < 1 ? 1 : page;
  const startIndex = (currentPage - 1) * pageSize;
  return rows.slice(startIndex, startIndex + pageSize);
}

function chargeRowFrom(
  invoice: Invoice,
  patientsById: Map<string, Patient>,
  organizationsById: Map<string, Organization>,
): PracticeLedgerRow | null {
  const statementNumber = statementNumberOf(invoice);
  if (statementNumber && predecessorNumber(statementNumber)) return null;

  const client = clientFieldsFrom(invoice, patientsById);
  if (!invoice.id || !client) return null;

  return {
    id: `Invoice/${invoice.id}`,
    ...client,
    statementId: statementNumber,
    type: PRACTICE_LEDGER_ROW_TYPE.Charge,
    transactionDate: toCalendarDate(invoice.date),
    ...sourceFieldsFrom(invoice, organizationsById),
    amount: invoice.totalNet?.value ?? invoice.totalGross?.value ?? 0,
  };
}

function detailRowFrom(
  paymentReconciliation: PaymentReconciliation,
  detail: PaymentReconciliationDetail,
  index: number,
  invoice: Invoice,
  patientsById: Map<string, Patient>,
  organizationsById: Map<string, Organization>,
): PracticeLedgerRow | null {
  const detailType = getDetailType(detail);
  if (!detailType) return null;

  const amount = detail.amount?.value;
  if (amount === undefined || amount === 0) return null;

  const client = clientFieldsFrom(invoice, patientsById);
  if (!client) return null;

  const reconciliationReference = paymentReconciliation.id
    ? joinReferenceString("PaymentReconciliation", paymentReconciliation.id)
    : "PaymentReconciliation";

  return {
    id: `${reconciliationReference}:${index}`,
    ...client,
    statementId: statementNumberOf(paymentReconciliation) ?? statementNumberOf(invoice),
    type: detailRowType(detailType, amount),
    transactionDate: toCalendarDate(
      paymentReconciliation.paymentDate ?? paymentReconciliation.created,
    ),
    ...paymentSourceFieldsFrom(paymentReconciliation, organizationsById),
    amount,
  };
}

function detailRowType(detailType: LedgerDetailType, amount: number): PracticeLedgerRowType {
  if (detailType === LEDGER_DETAIL_TYPE.Adjustment) return PRACTICE_LEDGER_ROW_TYPE.Adjustment;
  return isPaymentReversalDetail(detailType, amount)
    ? PRACTICE_LEDGER_ROW_TYPE.PaymentReversal
    : PRACTICE_LEDGER_ROW_TYPE.Payment;
}

function clientFieldsFrom(
  invoice: Invoice,
  patientsById: Map<string, Patient>,
): Pick<PracticeLedgerRow, "clientId" | "clientName" | "medicalRecordNumber"> | null {
  const clientId = resolveId(invoice.subject);
  if (!clientId) return null;

  const patient = patientsById.get(clientId);
  return {
    clientId,
    clientName: formatHumanName(patient?.name?.[0] ?? {}) || EMPTY_VALUE,
    medicalRecordNumber: (patient && getPatientMrn(patient)) || EMPTY_VALUE,
  };
}

function sourceFieldsFrom(
  invoice: Invoice,
  organizationsById: Map<string, Organization>,
): Pick<PracticeLedgerRow, "sourceLabel" | "sourceKind"> {
  const sourceLabel = getLedgerSourceLabel(invoice, organizationsById);
  const sourceKind =
    getLedgerSource(invoice) === LEDGER_SOURCE.Insurance
      ? PRACTICE_LEDGER_SOURCE_KIND.Insurance
      : PRACTICE_LEDGER_SOURCE_KIND.Client;

  return { sourceLabel, sourceKind };
}

function paymentSourceFieldsFrom(
  paymentReconciliation: PaymentReconciliation,
  organizationsById: Map<string, Organization>,
): Pick<PracticeLedgerRow, "sourceLabel" | "sourceKind"> {
  const entryLabel = getLedgerEntrySourceLabel(paymentReconciliation, organizationsById);
  if (entryLabel === LEDGER_SOURCE.Client || entryLabel === LEDGER_SOURCE.Other) {
    return { sourceLabel: entryLabel, sourceKind: PRACTICE_LEDGER_SOURCE_KIND.Client };
  }
  if (entryLabel === LEDGER_SOURCE.Practice) {
    return { sourceLabel: entryLabel, sourceKind: PRACTICE_LEDGER_SOURCE_KIND.Practice };
  }
  if (entryLabel) {
    return { sourceLabel: entryLabel, sourceKind: PRACTICE_LEDGER_SOURCE_KIND.Insurance };
  }

  const organizationId = getPaymentIssuerOrganizationId(paymentReconciliation);
  if (!organizationId && !paymentReconciliation.paymentIssuer) {
    return {
      sourceLabel: LEDGER_SOURCE.Client,
      sourceKind: PRACTICE_LEDGER_SOURCE_KIND.Client,
    };
  }

  const organizationName = organizationId
    ? organizationsById.get(organizationId)?.name?.trim()
    : undefined;
  const display = paymentReconciliation.paymentIssuer?.display?.trim();
  return {
    sourceLabel: organizationName || display || LEDGER_SOURCE.Insurance,
    sourceKind: PRACTICE_LEDGER_SOURCE_KIND.Insurance,
  };
}
